import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { GatewaySort, KitPlacement } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { parseOneOf, parsePage, parseQuery } from './params.ts'
import { rootRoute } from './root.tsx'

// The kits: what is on the bench, then every kit there is. Search, sort, view and page are
// typed search params (§4.4), so a filtered view is a link.

/** Which kits the list shows: a placement, the ones that want a look, or the ones behind. */
export type KitView = KitPlacement | 'attention' | 'outdated'

export const KIT_VIEWS: readonly KitView[] = [
  'in_stock',
  'installed',
  'attention',
  'outdated',
  'removed',
]

export interface KitSearch {
  /** 1-based: it is the number a person shares. */
  readonly page: number
  readonly q: string | null
  readonly sort: GatewaySort
  /** `null`: every kit, the retired ones included. */
  readonly view: KitView | null
}

const SORTS: readonly GatewaySort[] = [
  'installed_on',
  '-installed_on',
  'hardware_id',
  '-hardware_id',
  'last_seen_at',
  '-last_seen_at',
]

/** Newest installation first, which puts the kits in stock on top: where the bench wants them. */
const DEFAULT_KIT_SORT: GatewaySort = '-installed_on'

export const DEFAULT_KIT_SEARCH: KitSearch = {
  page: 1,
  q: null,
  sort: DEFAULT_KIT_SORT,
  view: null,
}

/** The kits as a link target: path and the search params its type requires, in one place. */
export const kitListLink = { to: '/kits', search: DEFAULT_KIT_SEARCH } as const

export const kitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kits',
  validateSearch: (search: Record<string, unknown>): KitSearch => ({
    page: parsePage(search.page),
    q: parseQuery(search.q),
    sort: parseOneOf(SORTS, search.sort) ?? DEFAULT_KIT_SORT,
    view: parseOneOf(KIT_VIEWS, search.view),
  }),
  search: {
    middlewares: [stripSearchParams(DEFAULT_KIT_SEARCH)],
  },
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/kits.tsx')),
})
